/**
 * Human labels for proposal tools, entity kinds and statuses.
 *
 * The same proposal is named in three places — the inline marker in the
 * transcript, the inspector panel header, and the card itself — and each used
 * to keep its own hardcoded English map. They drifted: the marker never learned
 * about link/unlink file and rendered the raw tool name, and none of the three
 * translated. One lookup, one set of keys, one place to add a tool.
 *
 * `defaultValue` is the raw tool name so a tool added on the backend before its
 * translation lands degrades to `propose_new_thing` rather than blank.
 */

export type Translate = (key: string, options?: any) => string;

export const toolLabelOf = (t: Translate, toolName: string): string =>
  t(`metaAgent.proposal.tool.${toolName}`, { defaultValue: toolName });

/** Sentence-case variant for panel headers ("Edit agent" vs "edit agent"). */
export const toolTitleOf = (t: Translate, toolName: string): string => {
  const label = toolLabelOf(t, toolName);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Label for a link/unlink target_type ('agent' | 'skill' | 'data_bundle'). */
export const linkKindLabelOf = (t: Translate, kind: string): string =>
  t(`metaAgent.proposal.linkKind.${kind}`, { defaultValue: kind });

/** Collapses the five stored statuses onto the three the user is shown. */
export const statusLabelOf = (t: Translate, status: string): string => {
  if (status === 'approved' || status === 'edited') return t('metaAgent.proposal.statusApplied');
  if (status === 'rejected') return t('metaAgent.proposal.statusRejected');
  if (status === 'timeout') return t('metaAgent.proposal.statusExpired');
  return t('metaAgent.proposal.statusPending');
};

/** The action category a tool belongs to — drives the card's icon + tint.
 *
 *  One glance answers "what KIND of thing is being asked?": green = something
 *  new, blue = a change, violet = a connection, amber = files moving, red =
 *  something going away. The specific entity is named by the title beside it.
 */
export type CardCategory = 'create' | 'edit' | 'connect' | 'disconnect' | 'files' | 'delete';

export const cardCategoryOf = (toolName: string): CardCategory => {
  if (toolName.startsWith('propose_delete')) return 'delete';
  if (toolName.startsWith('propose_detach') || toolName.startsWith('propose_unlink')) return 'disconnect';
  if (toolName.startsWith('propose_attach') || toolName.startsWith('propose_link')) return 'connect';
  if (toolName.startsWith('propose_create') || toolName === 'propose_duplicate_agent') return 'create';
  if (toolName === 'propose_add_file_to_folder' || toolName.startsWith('propose_manage_files')) return 'files';
  return 'edit';
};

/** A human title for the card header — "Connecting a folder to an agent",
 *  not `propose_link_library`.
 *
 *  Composed from the tool PLUS the payload, because the same tool serves
 *  several shapes (link_library carries folder-or-file to agent-or-skill-or-
 *  tenant) and the user asked for titles that say the actual shape. Falls
 *  back to the sentence-cased tool label for anything unmapped, so a new
 *  backend tool degrades to readable rather than blank.
 */
export const cardTitleOf = (
  t: Translate,
  toolName: string,
  payload?: Record<string, unknown> | null,
): string => {
  const p = payload || {};
  const linkKind = (k: unknown, fallback: string) =>
    linkKindLabelOf(t, typeof k === 'string' && k ? k : fallback);

  // Library links: folder-or-file → agent / skill / tenant context.
  if (toolName === 'propose_link_library' || toolName === 'propose_unlink_library') {
    const subjectKey = (p.node_kind === 'file' || p.file_id) ? 'file' : 'folder';
    const subject = t(`metaAgent.proposal.subject.${subjectKey}`);
    const target = linkKind(p.target_type, 'agent');
    return t(
      toolName === 'propose_link_library'
        ? 'metaAgent.proposal.cardTitle.connect'
        : 'metaAgent.proposal.cardTitle.disconnect',
      { subject, target },
    );
  }
  // Legacy file links: file → agent / skill / data bundle.
  if (toolName === 'propose_link_file' || toolName === 'propose_unlink_file') {
    const subject = t('metaAgent.proposal.subject.file');
    const target = linkKind(p.target_type, 'agent');
    return t(
      toolName === 'propose_link_file'
        ? 'metaAgent.proposal.cardTitle.connect'
        : 'metaAgent.proposal.cardTitle.disconnect',
      { subject, target },
    );
  }
  // Skill / bundle attachments.
  if (toolName.startsWith('propose_attach') || toolName.startsWith('propose_detach')) {
    const isBundle = toolName.includes('bundle');
    const subject = t(`metaAgent.proposal.subject.${isBundle ? 'data_bundle' : 'skill'}`);
    const target = linkKind(
      toolName.endsWith('_to_skill') || toolName.endsWith('_from_skill') ? 'skill' : 'agent',
      'agent',
    );
    return t(
      toolName.startsWith('propose_attach')
        ? 'metaAgent.proposal.cardTitle.connect'
        : 'metaAgent.proposal.cardTitle.disconnect',
      { subject, target },
    );
  }
  const direct = t(`metaAgent.proposal.cardTitle.${toolName}`, { defaultValue: '' });
  return direct || toolTitleOf(t, toolName);
};

/** Read/observe tools → a present-progressive step sentence for the live
 *  status line. Proposal tools reuse `cardTitleOf` (their gerund title IS the
 *  step); everything else is a lookup, and the unknown case degrades to a
 *  generic "Working…" rather than a raw snake_case name. */
const STEP_KEYS: Record<string, string> = {
  create_artifact: 'preparingDoc',
  edit_artifact: 'writingDoc',
  read_artifact: 'readingDoc',
  open_library_file: 'openingFile',
  read_library_file: 'readingLibraryFile',
  read_library_folder: 'browsingLibrary',
  list_library_folders: 'browsingLibrary',
  list_agents: 'lookingUpAgents',
  get_agent: 'lookingUpAgents',
  list_skills: 'lookingUpSkills',
  send_test_message_to_agent: 'testingAgent',
};

/** The live "what the agent is doing right now" sentence for one tool call. */
export const stepLabelForTool = (
  t: Translate,
  toolName?: string | null,
  args?: Record<string, unknown> | null,
): string => {
  const name = toolName || '';
  if (name.startsWith('propose_')) {
    // The proposal's own gerund title is the step ("Connecting a folder to an
    // agent"), so the live line reads the same as the card it will raise.
    const title = cardTitleOf(t, name, args || {});
    return t('metaAgent.chat.step.suffix', { label: title, defaultValue: `${title}…` });
  }
  const key = STEP_KEYS[name];
  if (key) return t(`metaAgent.chat.step.${key}`);
  return t('metaAgent.chat.step.working', { defaultValue: 'Working…' });
};

/** A human label for an edited field — "System prompt", not
 *  `chatbot_system_prompt`. Falls back to a title-cased de-snaked form so a new
 *  field degrades to readable rather than a raw column name. */
const FIELD_LABELS: Record<string, string> = {
  chatbot_system_prompt: 'System prompt',
  agent_rules: 'Rules',
  chatbot_description: 'Description',
  files_context_prompt: 'Files context',
  default_model: 'Model',
  chatbot_name: 'Name',
  handler_config: 'Configuration',
  mode_slider_config: 'Behavior modes',
  chatbot_config: 'Settings',
  enable_web_search: 'Web search',
  reveal_thinking: 'Show thinking',
  skill_system_prompt: 'Skill prompt',
  activation_prompt: 'Activation',
  skill_triggers: 'Triggers',
  skill_description: 'Description',
  skill_category: 'Category',
  bundle_context_prompt: 'Context',
  bundle_description: 'Description',
  folder_name: 'Folder name',
  file_display_name: 'File name',
};

export const fieldLabelOf = (t: Translate, field?: string | null): string => {
  if (!field) return '';
  const direct = t(`metaAgent.proposal.fieldLabel.${field}`, { defaultValue: '' });
  if (direct) return direct;
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  // Humanize an unmapped snake_case field: strip a leading entity prefix,
  // de-snake, and sentence-case.
  const cleaned = field.replace(/^(chatbot_|skill_|bundle_|agent_)/, '').replace(/_/g, ' ').trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : field;
};
